import { PapiDeviation } from '@wix/da-papi-types';
import LegacyMediaHandler from './LegacyHandler';
import MediaHandler from './MediaHandler';
import {
  IMediaService,
  MediaSrcFile,
  MediaSrcSet,
  ThumbSetOptions,
  DeviantMediaWrapper,
} from './types';
import { DeviationTypes } from '../../types/deviation';

/**
 * The media service encapsulates all media/file access to a
 * deviation object.
 *
 * This is a gradual refactor, we are moving various functions into a
 * single interface and then replacing the function calls 1-1.
 *
 * After this we will define a stricter public interface and make the
 * others private. This should give us a clear surface to work on.
 *
 * Once that is done we can add the new handler.
 */
class DeviationMediaService implements IMediaService {
  isGif(deviation: PapiDeviation): boolean {
    return this.handleFileOrMedia(deviation).isGif(deviation);
  }

  isPng(deviation: PapiDeviation): boolean {
    return this.handleFileOrMedia(deviation).isPng(deviation);
  }

  getDeviationType(deviation: PapiDeviation | undefined): DeviationTypes {
    if (!deviation) {
      return DeviationTypes.UNKNOWN;
    }
    return this.handleFileOrMedia(deviation).getDeviationType(deviation);
  }

  getFullviewForSize(
    deviation: PapiDeviation,
    width?: number,
    height?: number
  ): MediaSrcFile | undefined {
    return this.handleFileOrMedia(deviation).getFullviewForSize(deviation, width, height);
  }

  getSrcSetForDeviationPreview(
    deviation: PapiDeviation,
    isMobile: boolean,
    screenVw: string
  ): MediaSrcSet {
    return this.handleFileOrMedia(deviation).getSrcSetForDeviationPreview(
      deviation,
      isMobile,
      screenVw
    );
  }

  getLargestFullview(deviation: PapiDeviation): MediaSrcFile {
    return this.handleFileOrMedia(deviation).getLargestFullview(deviation);
  }

  getThumbDimensions(deviation: PapiDeviation) {
    return this.handleFileOrMedia(deviation).getThumbDimensions(deviation);
  }

  getSmallestThumb(deviation: PapiDeviation): MediaSrcFile | undefined {
    return this.handleFileOrMedia(deviation).getSmallestThumb(deviation);
  }

  getSmallestThumbThatFullyCovers(
    deviation: PapiDeviation,
    width: number,
    height: number,
    fallback?: (thumbs: MediaSrcFile[]) => MediaSrcFile,
    options?: ThumbSetOptions
  ): MediaSrcFile | undefined {
    return this.handleFileOrMedia(deviation).getSmallestThumbThatFullyCovers(
      deviation,
      width,
      height,
      fallback,
      options
    );
  }

  /**
   * TODO make this private
   *
   * @param deviation
   * @param fileType
   */
  getDeviationFile(deviation: DeviantMediaWrapper, fileType: string): MediaSrcFile | undefined {
    return this.handleFileOrMedia(deviation).getDeviationFile(deviation, fileType);
  }

  /**
   * Get default set of thumbs from the deviation
   */
  getThumbSet(deviation: PapiDeviation, options?: ThumbSetOptions): MediaSrcFile[] {
    return this.handleFileOrMedia(deviation).getThumbSet(deviation, options);
  }

  getMediaByType(deviation: DeviantMediaWrapper, fileType: string): MediaSrcFile[] {
    return this.handleFileOrMedia(deviation).getMediaByType(deviation, fileType);
  }

  getMediaFileType(deviation: DeviantMediaWrapper | undefined): string {
    if (!deviation) {
      return '';
    }
    return this.handleFileOrMedia(deviation).getMediaFileType(deviation);
  }

  private handleFileOrMedia(deviation: DeviantMediaWrapper): IMediaService {
    if (deviation && deviation.media) {
      return new MediaHandler();
    }
    return new LegacyMediaHandler();
  }

  getSmallestThumbForArtStage(
    deviation: PapiDeviation,
    width: number,
    height: number
  ): MediaSrcFile | undefined {
    return this.handleFileOrMedia(deviation).getSmallestThumbForArtStage(deviation, width, height);
  }
}

export default new DeviationMediaService();
